/* eslint-disable sonarjs/no-duplicate-string */
import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation, ElementRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, map, switchMap, take } from 'rxjs/operators';

import {
  ButtonSize,
  ButtonType,
  LoaderTheme,
  TabsComponent,
  TabTheme,
  ModalButtons,
  IconPosition,
} from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { LoaderType, ElementActions, ElementTypes } from '@fcom/common/interfaces';
import { AppState } from '@fcom/core';
import { Location } from '@fcom/core-api';
import { ProfileTier } from '@fcom/core-api/login';
import { profile } from '@fcom/core/selectors';
import { LocationRouteCffService } from '@fcom/core/services';
import { FormStatus, ReactiveFormStatus } from '@fcom/service-forms';
import { locationFormatValidator } from '@fcom/service-forms/services/form-validators';
import { AUTOCOMPLETE_DEBOUNCE_TIME } from '@fcom/service-forms/services/utils';
import { LocalDate, unsubscribe } from '@fcom/core/utils';
import { GtmService } from '@fcom/common/gtm';
import { GaContext } from '@fcom/common/interfaces/gtm.interface';

import { AmadeusError, FlightListSegment, FlightSegment } from '../../interfaces/upgrade-availability.interface';
import { UpgradeAvailabilityService } from '../../services/upgrade-availability.service';

interface NumericalFlightListSegment extends Omit<FlightListSegment, 'list_segment'> {
  // eslint-disable-next-line camelcase
  list_segment: Array<Omit<FlightSegment, 'available_seats'> & { available_seats: number }>;
}

@Component({
  selector: 'fin-upgrade-availability',
  templateUrl: 'upgrade-availability.component.html',
  styleUrls: ['./upgrade-availability.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class UpgradeAvailabilityComponent implements OnInit, OnDestroy {
  public readonly ButtonType = ButtonType;
  public readonly ButtonSize = ButtonSize;
  public readonly IconPosition = IconPosition;
  public readonly FormStatus = FormStatus;
  public readonly LoaderType = LoaderType;
  public readonly LoaderTheme = LoaderTheme;
  public readonly TabTheme = TabTheme;
  public readonly ModalButtons = ModalButtons;
  public readonly SvgLibraryIcon = SvgLibraryIcon;

  destinationLoading$: Subject<boolean> = new Subject();
  departureLoading$: Subject<boolean> = new Subject();
  destinationLocations$: Subject<Location[]> = new Subject();
  departureLocations$: Subject<Location[]> = new Subject();
  reactiveForm: UntypedFormGroup;
  maxDate: LocalDate = LocalDate.now().plusYears(1);
  minDate: LocalDate = LocalDate.now();
  flightList$: BehaviorSubject<NumericalFlightListSegment[]> = new BehaviorSubject([]);
  selectedDate: string;
  dateStringArray: string[];
  formStatus: FormStatus = FormStatus.INITIAL;
  errorMessage: string;
  profileTier$: Observable<ProfileTier>;
  isInfoModalOpen = false;

  @ViewChild('tabs', { static: false })
  tabs: TabsComponent;

  @ViewChild('openInfoModalButton', { static: false })
  openInfoModalButton: ElementRef;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private store$: Store<AppState>,
    private upgradeAvailabilityService: UpgradeAvailabilityService,
    private locationRouteCffService: LocationRouteCffService,
    private languageService: LanguageService,
    private gtmService: GtmService
  ) {}

  ngOnInit(): void {
    this.dateStringArray = this.getDates(this.minDate, this.maxDate);
    this.createForm();
    this.destinationLoading$.next(false);
    this.departureLoading$.next(false);
    this.subscriptions.add(
      this.flightList$.subscribe((list: NumericalFlightListSegment[]) => {
        if (list.length > 0 && this.tabs) {
          this.tabs.scrollToSelectedTab();
        }
      })
    );
    this.profileTier$ = this.store$.pipe(
      profile(),
      take(1),
      map((storeProfile) => storeProfile.tier)
    );
    this.onDestinationChanges();
    this.onDepartureChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  getFlightList(date = this.departureDate.value): void {
    if (this.reactiveForm.valid) {
      this.formStatus = FormStatus.LOADING;
      const locale = this.languageService.ddsLocaleValue;
      const departure = this.departure.value.locationCode;
      const destination = this.destination.value.locationCode;
      const departureDate: LocalDate = date;
      this.selectedDate = departureDate.toString();
      this.subscriptions.add(
        this.profileTier$
          .pipe(
            switchMap((profileTier) =>
              this.upgradeAvailabilityService.getUpgradeAvailability(
                profileTier,
                departure,
                destination,
                departureDate,
                locale.countrySite,
                locale.language
              )
            )
          )
          .subscribe((result: FlightListSegment[] | AmadeusError) => {
            if ((result as AmadeusError).message) {
              this.formStatus = FormStatus.ERROR;
              this.errorMessage = (result as AmadeusError).message;
              this.gtmService.trackElement(
                `error::${departure}-${destination}::${departureDate.toString()}`,
                GaContext.UPGRADE_AVAILABILITY_SEARCH,
                ElementTypes.BUTTON,
                undefined,
                ElementActions.SEARCH
              );
            } else {
              this.formStatus = FormStatus.COMPLETE;
              const filteredFlights = (result as FlightListSegment[])
                .filter((flights) => flights.display === 'true')
                .map((flightListSegment) => ({
                  ...flightListSegment,
                  // eslint-disable-next-line camelcase
                  list_segment: flightListSegment.list_segment.map((segment: FlightSegment) => ({
                    ...segment,
                    available_seats: parseInt(segment.available_seats, 10),
                  })),
                }));

              this.flightList$.next(filteredFlights);
              this.gtmService.trackElement(
                `success::${departure}-${destination}::${departureDate.toString()}`,
                GaContext.UPGRADE_AVAILABILITY_SEARCH,
                ElementTypes.BUTTON,
                undefined,
                ElementActions.SEARCH
              );
            }
          })
      );
    } else {
      this.reactiveForm.markAllAsTouched();
    }
  }

  getDates(startDate: LocalDate, stopDate: LocalDate): string[] {
    const dateArray = [];
    let currentDate = startDate;
    while (currentDate.lte(stopDate)) {
      dateArray.push(currentDate.toString());
      currentDate = currentDate.plusDays(1);
    }
    return dateArray;
  }

  bookNow(): void {
    const departure = this.departure.value.locationCode;
    const destination = this.destination.value.locationCode;
    const departureDate = this.departureDate.value?.toString();
    this.gtmService.trackElement(
      `success::${departure}-${destination}::${departureDate}`,
      GaContext.UPGRADE_AVAILABILITY_SEARCH,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );
    window.open(
      `/${this.languageService.langValue}/booking?origin=${departure}&destination=${destination}&departureDate=${departureDate}`,
      '_blank'
    );
  }

  openInfoModal(event: MouseEvent): void {
    event.stopPropagation();
    this.isInfoModalOpen = true;
  }

  closeInfoModal(): void {
    if (this.openInfoModalButton) {
      this.openInfoModalButton.nativeElement.focus();
    }
  }

  private createForm(): void {
    const formConfig = {
      departure: ['', [locationFormatValidator(), Validators.required]],
      destination: ['', [locationFormatValidator(), Validators.required]],
      departureDate: ['', [Validators.required]],
    };

    this.reactiveForm = this.fb.group(formConfig);
  }

  get departure() {
    return this.reactiveForm.get('departure');
  }

  get destination() {
    return this.reactiveForm.get('destination');
  }

  get departureDate() {
    return this.reactiveForm.get('departureDate');
  }

  private queryLocations(queryString: string, locations$: Subject<Location[]>, loading$: Subject<boolean>) {
    const locale = this.languageService.localeValue;

    loading$.next(true);

    this.subscriptions.add(
      this.locationRouteCffService.locationMatchesFor(queryString, locale).subscribe((matchedLocations) => {
        loading$.next(false);
        locations$.next(matchedLocations.items);
      })
    );
  }

  private onDepartureChanges(): void {
    this.subscriptions.add(
      this.departure.valueChanges.pipe(debounceTime(AUTOCOMPLETE_DEBOUNCE_TIME)).subscribe((val) => {
        if (this.reactiveForm.get('departure').status === ReactiveFormStatus.INVALID) {
          this.queryLocations(val, this.departureLocations$, this.departureLoading$);
        }
      })
    );
  }

  private onDestinationChanges(): void {
    this.subscriptions.add(
      this.destination.valueChanges.pipe(debounceTime(AUTOCOMPLETE_DEBOUNCE_TIME)).subscribe((val) => {
        if (this.reactiveForm.get('destination').status === ReactiveFormStatus.INVALID) {
          this.queryLocations(val, this.destinationLocations$, this.destinationLoading$);
        }
      })
    );
  }
}
